/**
 * The export for the accountant: one business, one period, one zip of CSV files.
 *
 * The export is made by the worker, so asking for one adds it to the list below, which polls
 * until it is ready (`exportsQuery`). The file is kept for a few days, then deleted; the list
 * says until when.
 */

import { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/api/problem";
import { Button, Field, Input, Notice, Select } from "@/components/ui/Field";
import { businessesQuery } from "@/features/settings/queries";
import { todayIso } from "@/lib/dates";
import { fieldMessage } from "@/lib/fieldMessage";
import { emptyExport, exportSchema, toExportBody, type ExportValues } from "./exportForm";
import { PERIODS } from "./filters";
import { ExportRow } from "./ExportRow";
import { exportsQuery, useRequestExport } from "./queries";

export function ExportPage() {
  const { t } = useTranslation();

  return (
    <section className="space-y-5">
      <Link
        to="/reports"
        className="-ml-1 inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-800"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden />
        {t("reports.title")}
      </Link>
      <div className="space-y-1">
        <h1 className="text-xl font-semibold text-stone-900">{t("reports.export.title")}</h1>
        <p className="text-sm text-stone-600">{t("reports.export.subtitle")}</p>
      </div>
      <ExportForm />
      <Exports />
    </section>
  );
}

function ExportForm() {
  const { t } = useTranslation();
  const businesses = useQuery(businessesQuery);
  const request = useRequestExport();
  const form = useForm<ExportValues>({
    resolver: zodResolver(exportSchema),
    defaultValues: emptyExport,
  });
  const errors = form.formState.errors;
  const period = useWatch({ control: form.control, name: "period" });

  // One business is the common case: choose it rather than make the user.
  useEffect(() => {
    const all = businesses.data ?? [];
    const only = all.length === 1 ? all[0] : undefined;
    if (only && !form.getValues("business_id")) form.setValue("business_id", only.id);
  }, [businesses.data, form]);

  const submit = form.handleSubmit(async (values) => {
    try {
      await request.mutateAsync(toExportBody(values, todayIso()));
    } catch (error) {
      form.setError("root", {
        type: "server",
        message: error instanceof ApiError ? error.message : t("reports.export.errors.failed"),
      });
    }
  });

  if (businesses.isSuccess && businesses.data.length === 0) {
    return <Notice tone="info">{t("reports.export.needsBusiness")}</Notice>;
  }

  return (
    <form
      onSubmit={submit}
      noValidate
      className="space-y-3 rounded-lg border border-stone-200 bg-white p-4"
    >
      <Field
        name="export-business"
        label={t("reports.business")}
        error={fieldMessage(errors, "business_id", t)}
      >
        <Select id="export-business" {...form.register("business_id")}>
          <option value="">{t("reports.export.chooseBusiness")}</option>
          {(businesses.data ?? []).map((business) => (
            <option key={business.id} value={business.id}>
              {business.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field name="export-period" label={t("reports.period")}>
        <Select id="export-period" {...form.register("period")}>
          {PERIODS.map((value) => (
            <option key={value} value={value}>
              {t(`reports.periods.${value}`)}
            </option>
          ))}
        </Select>
      </Field>
      {period === "custom" && (
        <div className="flex gap-3">
          <div className="min-w-0 flex-1">
            <Field
              name="export-start"
              label={t("reports.from")}
              error={fieldMessage(errors, "start", t)}
            >
              <Input id="export-start" type="date" {...form.register("start")} />
            </Field>
          </div>
          <div className="min-w-0 flex-1">
            <Field name="export-end" label={t("reports.to")} error={fieldMessage(errors, "end", t)}>
              <Input id="export-end" type="date" {...form.register("end")} />
            </Field>
          </div>
        </div>
      )}
      {errors.root?.message && <Notice tone="error">{errors.root.message}</Notice>}
      <Button type="submit" disabled={form.formState.isSubmitting}>
        {form.formState.isSubmitting ? t("common.saving") : t("reports.export.make")}
      </Button>
    </form>
  );
}

function Exports() {
  const { t } = useTranslation();
  const exports = useQuery(exportsQuery("csv"));

  if (exports.isPending) return <p className="text-stone-500">{t("common.loading")}</p>;
  if (exports.isError) return <Notice tone="error">{t("reports.export.errors.loadFailed")}</Notice>;
  if (exports.data.length === 0) return null;

  return (
    <div className="space-y-2">
      <h2 className="font-medium text-stone-800">{t("reports.export.recent")}</h2>
      <ul className="divide-y divide-stone-200 overflow-hidden rounded-lg border border-stone-200 bg-white">
        {exports.data.map((item) => (
          <ExportRow key={item.id} item={item} />
        ))}
      </ul>
      <p className="text-sm text-stone-500">{t("reports.export.contents")}</p>
    </div>
  );
}
